import { DateTime } from 'luxon';

import { DASH, toServerDateTime } from '@/globals';
import formatNumber from '@/utils/NumberFormatter';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

export interface ICachedPacingAPI {
  campaigncachedpacing_id?: number;
  lineitemcachedpacing_id?: number;
  average_cpm_buyer: number;
  average_cpm_owner: number;
  cached_report_last_updated_utc?: string;
  total_impressions: number;
  total_spend_buyer: number;
  total_spend_owner: number;
  pacing: number;
  progress: number;
}
export default class CachedPacing extends Validatable {
  averageCPMBuyer: number;

  averageCPMOwner: number;

  cachedReportLastUpdatedUtc?: DateTime;

  totalImpressions: number;

  totalSpendBuyer: number;

  totalSpendOwner: number;

  pacing: number;

  progress: number;

  constructor(
    id: number,
    averageCPMBuyer: number,
    averageCPMOwner: number,
    cachedReportLastUpdatedUtc: DateTime | null,
    totalImpressions: number,
    totalSpendBuyer: number,
    totalSpendOwner: number,
    pacing: number,
    progress: number,
  ) {
    super();
    this.id = id;
    this.averageCPMBuyer = averageCPMBuyer;
    this.averageCPMOwner = averageCPMOwner;
    this.cachedReportLastUpdatedUtc = cachedReportLastUpdatedUtc;
    this.totalImpressions = totalImpressions;
    this.totalSpendBuyer = totalSpendBuyer;
    this.totalSpendOwner = totalSpendOwner;
    this.pacing = pacing;
    this.progress = progress;
  }

  get averageCPMBuyerFormatted(): string {
    return this.averageCPMBuyer != null ? i18n.global.n(this.averageCPMBuyer, 'currency') : DASH;
  }

  get averageCPMOwnerFormatted(): string {
    return this.averageCPMOwner != null ? i18n.global.n(this.averageCPMOwner, 'currency') : DASH;
  }

  get totalImpressionsFormatted(): string {
    return this.totalImpressions != null ? formatNumber(this.totalImpressions, '0,0') : formatNumber(0, '0,0');
  }

  get totalImpressionsFormattedShort(): string {
    return this.totalImpressions != null ? formatNumber(this.totalImpressions, '0a') : formatNumber(0, '0,0');
  }

  get totalSpendBuyerFormatted(): string {
    return this.totalSpendBuyer != null ? i18n.global.n(this.totalSpendBuyer, 'currency') : DASH;
  }

  get totalSpendOwnerFormatted(): string {
    return this.totalSpendOwner != null ? i18n.global.n(this.totalSpendOwner, 'currency') : DASH;
  }

  get cachedReportLastUpdatedOnFormatted(): string {
    return this.cachedReportLastUpdatedUtc != null
      ? this.cachedReportLastUpdatedUtc.toLocal().toLocaleString(DateTime.DATETIME_SHORT)
      : DASH;
  }

  static fromApi(apiObject: ICachedPacingAPI): CachedPacing {
    return new CachedPacing(
      apiObject.campaigncachedpacing_id || apiObject.lineitemcachedpacing_id,
      apiObject.average_cpm_buyer,
      apiObject.average_cpm_owner,
      apiObject.cached_report_last_updated_utc != null
        ? DateTime.fromISO(apiObject.cached_report_last_updated_utc, { zone: 'utc' })
        : null,
      apiObject.total_impressions,
      apiObject.total_spend_buyer,
      apiObject.total_spend_owner,
      apiObject.pacing,
      apiObject.progress,
    );
  }

  toApi(): ICachedPacingAPI {
    return {
      campaigncachedpacing_id: this.id,
      average_cpm_buyer: this.averageCPMBuyer,
      average_cpm_owner: this.averageCPMOwner,
      cached_report_last_updated_utc: toServerDateTime(this.cachedReportLastUpdatedUtc),
      total_impressions: this.totalImpressions,
      total_spend_buyer: this.totalSpendBuyer,
      total_spend_owner: this.totalSpendOwner,
      pacing: this.pacing,
      progress: this.progress,
    };
  }
}
